import React from "react";
import { AlertCircle, ArrowRight, Video, Sparkles, Calendar } from "lucide-react";
import { Spinner } from "flowbite-react";

interface NoCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewPlans: () => void;
  type?: "participation" | "room";
}

export const NoCreditModal: React.FC<NoCreditModalProps> = ({
  isOpen,
  onClose,
  onViewPlans,
  type = "participation"
}) => {
  if (!isOpen) return null;

  const message = type === "room" 
    ? "Para entrar em salas ao vivo, você precisa ter créditos de participação disponíveis."
    : "Para agendar participação em sessões, você precisa ter créditos de participação disponíveis.";

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-50 transition-opacity"
        onClick={onClose}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              <h3 className="text-xl font-semibold text-gray-900">Crédito Necessário</h3>
            </div>
            
            <div className="text-center py-4">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">{message}</p>
                <p className="text-sm text-gray-600">
                  Adquira um plano de participação para começar a participar como observador ou representante.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem("redirectAfterPlanPurchase", window.location.pathname);
                  onClose();
                  onViewPlans();
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                Ver opções
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface ConfirmRoomEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
  participationCredits: number;
}

export const ConfirmRoomEntryModal: React.FC<ConfirmRoomEntryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing,
  participationCredits
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-50 transition-opacity"
        onClick={() => !isProcessing && onClose()}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Video className="w-5 h-5 text-purple-600" />
              <h3 className="text-xl font-semibold text-gray-900">Confirmar Entrada</h3>
            </div>
            
            <div className="text-center py-4">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  Ao entrar nesta sala ao vivo, 1 crédito de participação será debitado da sua conta.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-lg mt-3">
                  <Sparkles className="w-4 h-4 text-teal-600" />
                  <span className="text-sm font-medium text-teal-700">
                    Você possui {participationCredits} crédito{participationCredits !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className="px-4 py-2 bg-gradient-to-r from-[#A56096] to-[#8B4A7A] text-white rounded-lg hover:from-[#8B4A7A] hover:to-[#7A4169] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Spinner size="sm" />
                    Processando...
                  </>
                ) : (
                  <>
                    Confirmar e Entrar
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface ConfirmParticipationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
  participationCredits: number;
}

export const ConfirmParticipationModal: React.FC<ConfirmParticipationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing,
  participationCredits
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 z-50 transition-opacity"
        onClick={() => !isProcessing && onClose()}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-teal-600" />
              <h3 className="text-xl font-semibold text-gray-900">Confirmar Participação</h3>
            </div>
            
            <div className="text-center py-4">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  Ao agendar sua participação nesta sessão, 1 crédito de participação será debitado da sua conta.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-lg mt-3">
                  <Sparkles className="w-4 h-4 text-teal-600" />
                  <span className="text-sm font-medium text-teal-700">
                    Você possui {participationCredits} crédito{participationCredits !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className="px-4 py-2 bg-gradient-to-r from-[#3A5859] to-[#2C4344] text-white rounded-lg hover:from-[#2C4344] hover:to-[#1F2F30] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Spinner size="sm" />
                    Processando...
                  </>
                ) : (
                  <>
                    Confirmar Participação
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
